import { useVirtualizer } from "@tanstack/react-virtual";
import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getSelectedSpans,
  kDefaultExcludeEvents,
  kTranscriptCollapseScope,
  kTranscriptOutlineCollapseScope,
  spanHasBranches,
  TimelineSwimLanes,
  TranscriptOutline,
  useTimelineConfig,
  useTimelinesArray,
  useTranscriptTimeline,
} from "@tsmono/inspect-components/transcript";

import {
  defaultCollapsedSpanIds,
  syntheticEventsFromSkeleton,
  type ChunkedSample,
} from "../../../../log_data";
import { useStore } from "../../../../state/store";

import { ChunkedRowView, rowNodeId } from "./ChunkedRowView";
import styles from "./ChunkedTranscriptPanel.module.css";
import { outlineViewTree } from "./mainViewOutline";
import { useChunkedRows } from "./useChunkedRows";

interface ChunkedTranscriptPanelProps {
  id: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  offsetTop?: number;
  chunked: ChunkedSample;
}

/**
 * The transcript for a chunked-shape sample: a row-window list model over
 * the RowSpace (the forked seam named in design/large-samples.md — shared
 * per-event row renderers, second window model) plus the legacy timeline
 * presentation layer (swimlanes + main-view outline) fed by a synthetic
 * event stream reconstructed from the skeleton. Rows materialize as they
 * scroll into view; everything above the data layer is estimate-then-correct
 * with ordinal re-anchoring.
 *
 * Divergence from the legacy panel: swimlane/outline selection scrolls the
 * body to the selected span rather than re-scoping the body to that view —
 * the body renders the raw event tree (main-view body is a flagged spec
 * follow-up).
 */
export const ChunkedTranscriptPanel: FC<ChunkedTranscriptPanelProps> = ({
  id,
  scrollRef,
  offsetTop,
  chunked,
}) => {
  const hiddenTypesStored = useStore(
    (state) => state.sample.eventFilter.filteredTypes
  );
  // Chunked transcripts stream events lazily, so the dynamic default (which
  // inspects the full event list) is not computed here; static defaults apply.
  const hiddenTypes = useMemo(
    () => hiddenTypesStored ?? [...kDefaultExcludeEvents],
    [hiddenTypesStored]
  );
  const collapsedOverrides = useStore(
    (state) => state.sample.collapsedEvents?.[kTranscriptCollapseScope]
  );
  const collapseEvent = useStore((state) => state.sampleActions.collapseEvent);

  const defaultCollapsed = useMemo(
    () => defaultCollapsedSpanIds(chunked.skeleton),
    [chunked]
  );
  const rows = useChunkedRows(
    chunked,
    collapsedOverrides,
    defaultCollapsed,
    hiddenTypes
  );

  // ---------------------------------------------------------------------------
  // Timeline layer (legacy pipeline over the synthetic skeleton stream)
  // ---------------------------------------------------------------------------

  const synth = useMemo(
    () => syntheticEventsFromSkeleton(chunked.skeleton),
    [chunked]
  );
  // TranscriptLayout.eventsForTimeline: anchors exempt from the type filter
  const eventsForTimeline = useMemo(
    () =>
      synth.events.filter(
        (e) => e.event === "anchor" || !hiddenTypes.includes(e.event)
      ),
    [synth, hiddenTypes]
  );

  const timelinesForBranchDetection = useTimelinesArray(eventsForTimeline);
  const branchesPresent = useMemo(
    () => timelinesForBranchDetection.some((tl) => spanHasBranches(tl.root)),
    [timelinesForBranchDetection]
  );
  const timelineConfig = useTimelineConfig({ branchesPresent });

  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const timelineSelection = useMemo(
    () => ({
      selected: selectedRow,
      onSelect: (key: string | null) => setSelectedRow(key),
    }),
    [selectedRow]
  );

  const {
    state: timelineState,
    swimlanes,
    minimap,
    selection,
    hasTimeline,
    hasAgentTimeline,
  } = useTranscriptTimeline({
    events: eventsForTimeline,
    markerConfig: timelineConfig.markerConfig,
    timelineOptions: timelineConfig.agentConfig,
    timelineProps: timelineSelection,
  });

  const outlineTree = useMemo(
    () => outlineViewTree(selection.events, selection.sourceSpans, hiddenTypes),
    [selection.events, selection.sourceSpans, hiddenTypes]
  );

  const outlineCollapsed = useStore(
    (state) => state.sample.collapsedEvents?.[kTranscriptOutlineCollapseScope]
  );
  const setCollapsedEventsStore = useStore(
    (state) => state.sampleActions.setCollapsedEvents
  );
  const outlineCollapse = useMemo(
    () => ({
      collapsed: outlineCollapsed,
      onCollapse: (nodeId: string, collapsed: boolean) =>
        collapseEvent(kTranscriptOutlineCollapseScope, nodeId, collapsed),
      onSetCollapsed: (ids: Record<string, boolean>) =>
        setCollapsedEventsStore(kTranscriptOutlineCollapseScope, ids),
    }),
    [outlineCollapsed, collapseEvent, setCollapsedEventsStore]
  );

  // TranscriptLayout's auto default: collapse when there's no agent
  // sub-structure to drill into.
  const swimlanesDefaultCollapsed = hasTimeline ? !hasAgentTimeline : true;

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual returns fresh closures by design, so the compiler declines to memoize this component; the virtualizer stays local to this file and is never handed to a memoized child, so the bail-out costs nothing here
  const virtualizer = useVirtualizer({
    count: rows.total,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 64,
    overscan: 8,
    paddingStart: offsetTop,
    getItemKey: (index) => {
      const slot = rows.slotAt(index);
      return slot.kind === "row" ? rowNodeId(slot.row) : `ph-${index}`;
    },
  });

  // materialize the chunks under visible placeholders (idempotent)
  const items = virtualizer.getVirtualItems();
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    for (const item of items) {
      const slot = rows.slotAt(item.index);
      if (slot.kind === "placeholder") {
        rows.materialize(slot.chunkIdx);
      }
    }
  }, [items, rows]);

  // Ordinal anchoring (spec amendment 3): row-count corrections above the
  // viewport shift content, so track the topmost visible ordinal and
  // re-scroll to its corrected row index whenever accounting changes.
  const anchorRef = useRef<number | undefined>(undefined);
  const versionRef = useRef(rows.version);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (rows.version !== versionRef.current) {
      versionRef.current = rows.version;
      if (anchorRef.current !== undefined) {
        virtualizer.scrollToIndex(rows.rowIndexForOrdinal(anchorRef.current), {
          align: "start",
        });
      }
    }
  });
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const first = items.find((item) => rows.slotAt(item.index).kind === "row");
    if (first !== undefined) {
      const slot = rows.slotAt(first.index);
      if (slot.kind === "row") {
        anchorRef.current = slot.row.ordinal;
      }
    }
  }, [items, rows]);

  const eventCallbacks = useMemo(
    () => ({
      // Span ids key row accounting (RowSpace); anything else is an
      // event-level panel id (uuid) that collapses within its own panel —
      // answered straight from the store overrides.
      getCollapsed: (nodeId: string) =>
        chunked.skel.spanIds.has(nodeId)
          ? rows.isCollapsed(nodeId)
          : (collapsedOverrides?.[nodeId] ?? false),
      onCollapse: (nodeId: string, collapsed: boolean) => {
        collapseEvent(kTranscriptCollapseScope, nodeId, collapsed);
      },
    }),
    [chunked, rows, collapsedOverrides, collapseEvent]
  );

  const jumpToOrdinal = useCallback(
    (ordinal: number) => {
      anchorRef.current = ordinal;
      virtualizer.scrollToIndex(rows.rowIndexForOrdinal(ordinal), {
        align: "start",
      });
    },
    [virtualizer, rows]
  );

  // Swimlane selection: scroll the body to the selected span's begin
  // ordinal (the body always renders the full raw tree — see docstring).
  const spanBeginByid = useMemo(
    () =>
      new Map(
        chunked.skeleton.spans.map((span) => [span.id, span.begin] as const)
      ),
    [chunked]
  );
  const handleRowSelect = useCallback(
    (key: string | null) => {
      setSelectedRow(key);
      if (key === null) return;
      const span = getSelectedSpans(timelineState.rows, key)[0];
      const begin = span !== undefined ? spanBeginByid.get(span.id) : undefined;
      if (begin !== undefined) jumpToOrdinal(begin);
    },
    [timelineState.rows, spanBeginByid, jumpToOrdinal]
  );
  const swimlaneNavigation = useMemo(
    () => ({
      node: timelineState.node,
      selected: timelineState.selected,
      select: handleRowSelect,
      clearSelection: () => setSelectedRow(null),
    }),
    [timelineState.node, timelineState.selected, handleRowSelect]
  );

  const swimlaneHeader = useMemo(
    () => ({ minimap, timelineConfig }),
    [minimap, timelineConfig]
  );

  // Outline navigation: node ids are synthetic uuids; map to ordinals.
  const [selectedOutlineId, setSelectedOutlineId] = useState<string | null>(
    null
  );
  const navigateToOutlineEvent = useCallback(
    (eventId: string) => {
      const ordinal = synth.ordinals.get(eventId);
      if (ordinal !== undefined) jumpToOrdinal(ordinal);
    },
    [synth, jumpToOrdinal]
  );
  const [outlineScrollEl, setOutlineScrollEl] = useState<HTMLDivElement | null>(
    null
  );

  return (
    <div data-testid={`${id}-chunked`}>
      {hasTimeline && (
        <div className={styles.swimlanes} style={{ top: offsetTop ?? 0 }}>
          <TimelineSwimLanes
            layouts={swimlanes.layouts}
            timeline={swimlaneNavigation}
            header={swimlaneHeader}
            defaultCollapsed={swimlanesDefaultCollapsed}
            regionCounts={swimlanes.regionCounts}
            highlightedKeys={swimlanes.highlightedKeys}
          />
        </div>
      )}
      <div className={styles.layout}>
        <div
          ref={setOutlineScrollEl}
          className={styles.outline}
          style={{ top: offsetTop ?? 0, maxHeight: "80vh" }}
        >
          <TranscriptOutline
            eventNodes={outlineTree.eventNodes}
            defaultCollapsedIds={outlineTree.defaultCollapsedIds}
            scrollRef={scrollRef}
            outlineScrollEl={outlineScrollEl}
            agentName={selection.rowName}
            onNavigateToEvent={navigateToOutlineEvent}
            collapse={outlineCollapse}
            selectedOutlineId={selectedOutlineId}
            setSelectedOutlineId={setSelectedOutlineId}
          />
        </div>
        <div
          className={styles.list}
          style={{ height: virtualizer.getTotalSize() }}
        >
          {items.map((item) => {
            const slot = rows.slotAt(item.index);
            return (
              <div
                key={item.key}
                data-index={item.index}
                ref={virtualizer.measureElement}
                className={styles.row}
                // Position with `top`, not `transform`: a transformed ancestor
                // is a containing block that traps `position: sticky`
                // descendants, pinning the event-panel header to the card's
                // bottom instead of the scroll container's top. The monolith
                // VirtualList positions rows the same way for the same reason.
                style={{ top: item.start }}
              >
                {slot.kind === "row" ? (
                  <div
                    style={{
                      paddingLeft: `${
                        slot.row.depth <= 1
                          ? slot.row.depth * 0.7
                          : (0.7 + slot.row.depth - 1) * 1
                      }em`,
                    }}
                  >
                    <ChunkedRowView
                      row={slot.row}
                      eventCallbacks={eventCallbacks}
                    />
                  </div>
                ) : (
                  <div className={styles.placeholder} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
